import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Divider,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Snackbar
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Receipt as ReceiptIcon,
  Cancel as CancelIcon,
  LocalShipping as ShippingIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { getOrderById, cancelOrder, getShippingCompanies } from '../../services/storeService';
import { Order, OrderStatus, PaymentStatus, ShippingCompany } from '../../types/store';
import { BOSTA_STATE_MAP } from '../../types/bosta';
import { Link as RouterLink } from 'react-router-dom';

// ترجمة حالة الطلب
const translateOrderStatus = (status?: OrderStatus) => {
  if (!status) return 'غير معروف';

  switch (status) {
    case OrderStatus.PENDING:
      return 'قيد الانتظار';
    case OrderStatus.PROCESSING:
      return 'قيد المعالجة';
    case OrderStatus.SHIPPED:
      return 'تم الشحن';
    case OrderStatus.DELIVERED:
      return 'تم التوصيل';
    case OrderStatus.CANCELLED:
      return 'ملغي';
    default:
      return status || 'غير معروف';
  }
};

// ترجمة حالة الدفع
const translatePaymentStatus = (status?: PaymentStatus) => {
  if (!status) return 'غير معروف';

  switch (status) {
    case PaymentStatus.PAID:
      return 'مدفوع';
    case PaymentStatus.PENDING:
      return 'قيد الانتظار';
    case PaymentStatus.FAILED:
      return 'فشل الدفع';
    case PaymentStatus.REFUNDED:
      return 'تم رد المبلغ';
    default:
      return status || 'غير معروف';
  }
};

// تحديد لون حالة الطلب
const getOrderStatusColor = (status?: OrderStatus) => {
  if (!status) return 'default';

  switch (status) {
    case OrderStatus.PENDING:
      return 'warning';
    case OrderStatus.PROCESSING:
      return 'info';
    case OrderStatus.SHIPPED:
      return 'primary';
    case OrderStatus.DELIVERED:
      return 'success';
    case OrderStatus.CANCELLED:
      return 'error';
    default:
      return 'default';
  }
};

// تحديد لون حالة الدفع
const getPaymentStatusColor = (status?: PaymentStatus) => {
  if (!status) return 'default';

  switch (status) {
    case PaymentStatus.PAID:
      return 'success';
    case PaymentStatus.PENDING:
      return 'warning';
    case PaymentStatus.FAILED:
      return 'error';
    case PaymentStatus.REFUNDED:
      return 'info';
    default:
      return 'default';
  }
};

const OrderDetailPage: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  const [cancellationReason, setCancellationReason] = useState('');
  const [cancelLoading, setCancelLoading] = useState(false);
  const [shippingCompanies, setShippingCompanies] = useState<ShippingCompany[]>([]);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  });

  const fetchOrderDetails = async () => {
    if (!orderId) {
      setError('معرف الطلب غير صالح');
      setLoading(false);
      return;
    }

    if (!currentUser) {
      setError('يرجى تسجيل الدخول لعرض تفاصيل الطلب');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      console.log('Fetching order details for ID:', orderId);

      const orderData = await getOrderById(orderId);

      // التحقق من أن الطلب ينتمي للمستخدم الحالي
      if (orderData.customerId !== currentUser.id) {
        setError('ليس لديك صلاحية لعرض هذا الطلب');
        setLoading(false);
        return;
      }

      setOrder(orderData);
      setError(null);
    } catch (err: any) {
      console.error('Error fetching order details:', err);
      const errorMessage = err?.message || 'حدث خطأ أثناء تحميل تفاصيل الطلب';
      setError(errorMessage === 'Order not found' ? 'لم يتم العثور على الطلب' : 'حدث خطأ أثناء تحميل تفاصيل الطلب');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrderDetails();
    // تحميل شركات الشحن لعرض الأسماء
    getShippingCompanies()
      .then(companies => setShippingCompanies(companies))
      .catch(() => {});
  }, [orderId, currentUser]);

  const handleGoBack = () => {
    navigate('/profile');
  };

  // فتح نافذة حوار إلغاء الطلب
  const handleOpenCancelDialog = () => {
    setOpenCancelDialog(true);
  };

  // إغلاق نافذة حوار إلغاء الطلب
  const handleCloseCancelDialog = () => {
    setOpenCancelDialog(false);
    setCancellationReason('');
  };

  // إلغاء الطلب
  const handleCancelOrder = async () => {
    if (!orderId || !cancellationReason.trim()) {
      setSnackbar({
        open: true,
        message: 'يرجى إدخال سبب الإلغاء',
        severity: 'error'
      });
      return;
    }

    try {
      setCancelLoading(true);
      await cancelOrder(orderId, cancellationReason);

      // إعادة تحميل تفاصيل الطلب
      await fetchOrderDetails();

      handleCloseCancelDialog();

      setSnackbar({
        open: true,
        message: 'تم إلغاء الطلب بنجاح',
        severity: 'success'
      });
    } catch (err: any) {
      console.error('Error cancelling order:', err);
      setSnackbar({
        open: true,
        message: err?.message || 'حدث خطأ أثناء إلغاء الطلب',
        severity: 'error'
      });
    } finally {
      setCancelLoading(false);
    }
  };

  // إغلاق رسالة التنبيه
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // الحصول على اسم شركة الشحن من المعرّف
  const getShippingCompanyName = (methodId?: string): string => {
    if (!methodId) return 'غير متوفر';
    if (methodId === 'bosta') return 'Bosta Express';
    const company = shippingCompanies.find(c => c.id === methodId);
    return company?.name || methodId;
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 8, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error || !order) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || 'لم يتم العثور على الطلب'}
        </Alert>
        <Button
          variant="contained"
          startIcon={<ArrowBackIcon />}
          onClick={handleGoBack}
        >
          العودة إلى الملف الشخصي
        </Button>
      </Container>
    );
  }

  // تنسيق التاريخ
  const formatDate = (date: any) => {
    if (!date) return 'تاريخ غير متوفر';

    try {
      let dateObj: Date;

      // التحقق من نوع البيانات
      if (date instanceof Date) {
        dateObj = date;
      } else if (date.toDate && typeof date.toDate === 'function') {
        // Firestore Timestamp
        dateObj = date.toDate();
      } else if (date.seconds && date.nanoseconds) {
        // Firestore Timestamp في شكل كائن
        dateObj = new Date(date.seconds * 1000 + date.nanoseconds / 1000000);
      } else if (typeof date === 'string') {
        // سلسلة نصية
        dateObj = new Date(date);
      } else if (typeof date === 'number') {
        // طابع زمني بالميلي ثانية
        dateObj = new Date(date);
      } else {
        return 'تاريخ غير متوفر';
      }

      // التحقق من صحة التاريخ
      if (isNaN(dateObj.getTime())) {
        console.warn('Invalid date:', date);
        return 'تاريخ غير متوفر';
      }

      // تنسيق التاريخ بالتنسيق العربي
      const options: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      };

      return dateObj.toLocaleDateString('ar-EG', options);
    } catch (err) {
      console.error('Error formatting date:', err);
      return 'تاريخ غير متوفر';
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
      {/* زر العودة وعنوان الصفحة */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={handleGoBack}
          sx={{ ml: 2 }}
        >
          العودة
        </Button>
        <Typography variant="h4" component="h1">
          تفاصيل الطلب #{order.orderNumber || order.id.substring(0, 8)}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        {/* زر إلغاء الطلب - يظهر فقط إذا كان الطلب قيد الانتظار أو قيد المعالجة */}
        {(order.orderStatus === OrderStatus.PENDING || order.orderStatus === OrderStatus.PROCESSING) && (
          <Button
            variant="outlined"
            color="error"
            startIcon={<CancelIcon />}
            onClick={handleOpenCancelDialog}
          >
            إلغاء الطلب
          </Button>
        )}
      </Box>

      <Grid container spacing={3}>
        {/* معلومات الطلب الأساسية */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                <ReceiptIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                معلومات الطلب
              </Typography>
              <Box>
                <Chip
                  label={translateOrderStatus(order.orderStatus)}
                  color={getOrderStatusColor(order.orderStatus) as any}
                  sx={{ ml: 1 }}
                />
                <Chip
                  label={translatePaymentStatus(order.paymentStatus)}
                  color={getPaymentStatusColor(order.paymentStatus) as any}
                />
              </Box>
            </Box>
            <Divider sx={{ mb: 2 }} />

            {/* عرض سبب الإلغاء إذا كان الطلب ملغيًا */}
            {order.orderStatus === OrderStatus.CANCELLED && order.cancellationReason && (
              <Box sx={{ mb: 2, p: 2, bgcolor: '#FFF4F4', borderRadius: 1, border: '1px solid #FFCDD2' }}>
                <Typography variant="subtitle1" color="error" gutterBottom>
                  <CancelIcon sx={{ verticalAlign: 'middle', mr: 1, fontSize: 20 }} />
                  سبب الإلغاء:
                </Typography>
                <Typography variant="body1">
                  {order.cancellationReason}
                </Typography>
              </Box>
            )}

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="subtitle2" gutterBottom>رقم الطلب:</Typography>
                <Typography variant="body1">{order.orderNumber || order.id.substring(0, 8)}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="subtitle2" gutterBottom>تاريخ الطلب:</Typography>
                <Typography variant="body1">{formatDate(order.createdAt)}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="subtitle2" gutterBottom>طريقة الدفع:</Typography>
                <Typography variant="body1">{order.paymentMethod || 'غير محدد'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="subtitle2" gutterBottom>إجمالي الطلب:</Typography>
                <Typography variant="body1" fontWeight="bold">
                  {order.total.toLocaleString('ar-EG')} ج.م
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* معلومات العميل والشحن */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>معلومات العميل</Typography>
            <Divider sx={{ mb: 2 }} />
            <List dense>
              <ListItem>
                <ListItemText
                  primary="الاسم"
                  secondary={order.customerName}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="رقم الهاتف"
                  secondary={order.customerPhone}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="البريد الإلكتروني"
                  secondary={order.customerEmail || 'غير متوفر'}
                />
              </ListItem>
            </List>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>معلومات الشحن</Typography>
            <Divider sx={{ mb: 2 }} />
            <List dense>
              <ListItem>
                <ListItemText
                  primary="العنوان"
                  secondary={order.shippingDetails?.address || 'غير متوفر'}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="المدينة"
                  secondary={order.shippingDetails?.city || 'غير متوفر'}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="المنطقة"
                  secondary={order.shippingDetails?.region || 'غير متوفر'}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="طريقة الشحن"
                  secondary={getShippingCompanyName(order.shippingDetails?.shippingMethod)}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="تكلفة الشحن"
                  secondary={`${(order.shippingCost || order.shippingDetails?.shippingCost || 0).toLocaleString('ar-EG')} ج.م`}
                />
              </ListItem>
              {order.bostaTrackingNumber && (
                <>
                  <ListItem>
                    <ListItemText
                      primary="رقم تتبع Bosta"
                      secondary={
                        <Box component={RouterLink} to={`/tracking/${order.bostaTrackingNumber}`}
                          sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                          <ShippingIcon sx={{ fontSize: 14, ml: 0.5, verticalAlign: 'middle' }} />
                          {order.bostaTrackingNumber}
                        </Box>
                      }
                    />
                  </ListItem>
                  {order.bostaStateName && (
                    <ListItem>
                      <ListItemText
                        primary="حالة الشحنة"
                        secondary={
                          <Chip label={order.bostaStateName} size="small"
                            color={order.bostaState === 45 ? 'success' : order.bostaState === 49 ? 'error' : 'primary'} />
                        }
                      />
                    </ListItem>
                  )}
                </>
              )}
            </List>
          </Paper>
        </Grid>

        {/* تفاصيل المنتجات */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>المنتجات</Typography>
            <Divider sx={{ mb: 2 }} />

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>المنتج</TableCell>
                    <TableCell align="center">السعر</TableCell>
                    <TableCell align="center">الكمية</TableCell>
                    <TableCell align="left">الإجمالي</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {order.items.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>{item.productName}</TableCell>
                      <TableCell align="center">{item.price.toLocaleString('ar-EG')} ج.م</TableCell>
                      <TableCell align="center">{item.quantity}</TableCell>
                      <TableCell align="left">{item.total.toLocaleString('ar-EG')} ج.م</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ mt: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body1">
                    <strong>المجموع الفرعي:</strong> {order.subtotal.toLocaleString('ar-EG')} ج.م
                  </Typography>
                  <Typography variant="body1">
                    <strong>تكلفة الشحن:</strong> {order.shippingCost.toLocaleString('ar-EG')} ج.م
                  </Typography>
                  {order.discount > 0 && (
                    <Typography variant="body1">
                      <strong>الخصم:</strong> {order.discount.toLocaleString('ar-EG')} ج.م
                    </Typography>
                  )}
                  {order.tax > 0 && (
                    <Typography variant="body1">
                      <strong>الضريبة:</strong> {order.tax.toLocaleString('ar-EG')} ج.م
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="h6" align="right">
                    <strong>الإجمالي:</strong> {order.total.toLocaleString('ar-EG')} ج.م
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Grid>

        {/* ملاحظات الطلب */}
        {order.notes && (
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>ملاحظات</Typography>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="body1">{order.notes}</Typography>
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* نافذة حوار إلغاء الطلب */}
      <Dialog
        open={openCancelDialog}
        onClose={handleCloseCancelDialog}
        aria-labelledby="cancel-order-dialog-title"
      >
        <DialogTitle id="cancel-order-dialog-title">
          إلغاء الطلب
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            يرجى إدخال سبب إلغاء الطلب. لا يمكن إلغاء الطلب بعد شحنه.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="cancellation-reason"
            label="سبب الإلغاء"
            type="text"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={cancellationReason}
            onChange={(e) => setCancellationReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCancelDialog} color="inherit">
            إلغاء
          </Button>
          <Button
            onClick={handleCancelOrder}
            color="error"
            variant="contained"
            disabled={cancelLoading || !cancellationReason.trim()}
          >
            {cancelLoading ? <CircularProgress size={24} /> : 'تأكيد الإلغاء'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* رسالة التنبيه */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default OrderDetailPage;
